/* EDMT Liner Notes player. No analytics events or external player library. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('edmt-preview')) return;
  const players = new Set();
  const soundCloudWidgets = new Map();
  let channel;
  try { channel = new BroadcastChannel('edmt-preview-audio'); } catch (_) { /* Single-page coordination remains available. */ }

  const stopPlayers = (except) => players.forEach(player => { if (player !== except) player.pause(); });
  if (channel) channel.onmessage = () => {
    stopPlayers();
    pauseSoundCloud();
    document.dispatchEvent(new CustomEvent('edmt:preview-start'));
  };
  const pauseSoundCloud = () => soundCloudWidgets.forEach(widget => widget.pause());
  document.addEventListener('edmt:card-preview-start', () => {
    stopPlayers();
    pauseSoundCloud();
    if (channel) channel.postMessage('play');
  });
  document.addEventListener('play', event => {
    if (event.target instanceof HTMLMediaElement && !event.target.closest('edmt-preview') && !event.target.muted) { stopPlayers(); pauseSoundCloud(); }
  }, true);
  window.addEventListener('pagehide', () => stopPlayers());

  class EdmtPreview extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.audio = this.querySelector('audio');
      this.toggle = this.querySelector('.edmt-player__toggle');
      this.seek = this.querySelector('.edmt-player__seek');
      this.muteButton = this.querySelector('.edmt-player__mute');
      this.volume = this.querySelector('.edmt-player__volume');
      this.status = this.querySelector('.edmt-player__status');
      this.current = this.querySelector('[data-current]');
      this.total = this.querySelector('[data-total]');
      if (!this.audio || !this.toggle || !this.seek || !this.volume || !this.muteButton) {
        this.classList.add('is-fallback');
        return;
      }
      this.abort = new AbortController();
      const on = (target, name, handler) => target.addEventListener(name, handler, { signal: this.abort.signal });
      this.duration = Number(this.dataset.duration) || 0;
      this.intentToPlay = false;
      this.playVersion = 0;
      this.pendingSeek = null;
      this.dragging = false;
      this.audio.controls = false;
      this.audio.volume = 1;
      this.querySelectorAll('button,input').forEach(control => { control.disabled = false; });
      this.classList.remove('is-fallback');
      this.dataset.enhanced = 'true';
      this.setState('ready');
      this.syncVolume();
      this.updateTime();
      players.add(this);

      on(this.toggle, 'click', () => this.intentToPlay || !this.audio.paused ? this.pause() : this.play());
      on(this.muteButton, 'click', () => {
        if (this.audio.volume === 0) this.audio.volume = 1;
        this.audio.muted = !this.audio.muted;
        this.syncVolume();
      });
      on(this.volume, 'input', () => {
        this.audio.volume = Number(this.volume.value);
        this.audio.muted = this.audio.volume === 0;
        this.syncVolume();
      });
      // Native range provides touch, pointer, arrows, Home/End and accessibility semantics.
      on(this.seek, 'pointerdown', () => { this.dragging = true; });
      on(this.seek, 'pointerup', () => { this.dragging = false; });
      on(window, 'pointerup', () => { this.dragging = false; });
      on(this.seek, 'pointercancel', () => { this.dragging = false; this.updateTime(); });
      on(this.seek, 'blur', () => { this.dragging = false; });
      on(this.seek, 'input', () => this.seekTo(Number(this.seek.value)));
      on(this.seek, 'keydown', event => {
        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.seekTo(event.key === 'Home' ? 0 : this.duration);
          return;
        }
        const adjustments = { ArrowRight: 5, ArrowUp: 5, ArrowLeft: -5, ArrowDown: -5, PageUp: 15, PageDown: -15 };
        if (Object.prototype.hasOwnProperty.call(adjustments, event.key)) {
          event.preventDefault();
          this.seekTo(Number(this.seek.value) + adjustments[event.key]);
        }
      });
      on(this.audio, 'loadedmetadata', () => {
        if (Number.isFinite(this.audio.duration) && this.audio.duration > 0) this.duration = this.audio.duration;
        this.seek.max = String(this.duration);
        if (this.pendingSeek !== null) {
          this.audio.currentTime = Math.min(this.pendingSeek, this.duration);
          this.pendingSeek = null;
        }
        this.updateTime();
      });
      on(this.audio, 'timeupdate', () => { if (!this.dragging) this.updateTime(); });
      on(this.audio, 'durationchange', () => {
        if (Number.isFinite(this.audio.duration) && this.audio.duration > 0) {
          this.duration = this.audio.duration;
          this.seek.max = String(this.duration);
          this.updateTime();
        }
      });
      on(this.audio, 'play', () => this.claimPlayback());
      on(this.audio, 'playing', () => {
        if (!this.intentToPlay) { this.audio.pause(); return; }
        this.setState('playing');
      });
      on(this.audio, 'waiting', () => { if (this.intentToPlay) this.setState('loading'); });
      on(this.audio, 'loadstart', () => { if (this.intentToPlay) this.setState('loading'); });
      on(this.audio, 'seeking', () => { if (this.intentToPlay) this.setState('loading'); });
      on(this.audio, 'seeked', () => {
        this.updateTime();
        if (!this.intentToPlay) this.setState(this.audio.currentTime ? 'paused' : 'ready');
      });
      on(this.audio, 'pause', () => {
        if (!this.audio.ended) {
          this.intentToPlay = false;
          this.setState(this.audio.currentTime ? 'paused' : 'ready');
        }
      });
      on(this.audio, 'ended', () => {
        this.intentToPlay = false;
        this.updateTime();
        this.setState('ended');
      });
      on(this.audio, 'error', () => {
        this.intentToPlay = false;
        this.setState('error');
      });
      on(this.audio, 'volumechange', () => this.syncVolume());
    }

    disconnectedCallback() {
      this.pause();
      if (this.abort) this.abort.abort();
      this.abort = null;
      players.delete(this);
    }

    claimPlayback() {
      stopPlayers(this);
      pauseSoundCloud();
      // Existing product-card Audio instances are detached, so use the preview bridge.
      document.dispatchEvent(new CustomEvent('edmt:preview-start'));
      document.querySelectorAll('audio, video').forEach(media => {
        if (media !== this.audio && !media.muted && !media.paused) media.pause();
      });
      if (channel) channel.postMessage('play');
    }

    async play() {
      const version = ++this.playVersion;
      this.intentToPlay = true;
      if (this.audio.error) this.audio.load();
      if (this.audio.ended) this.audio.currentTime = 0;
      this.claimPlayback();
      this.setState('loading');
      try {
        await this.audio.play();
        if (version !== this.playVersion || !this.intentToPlay) this.audio.pause();
      } catch (error) {
        if (version !== this.playVersion || error.name === 'AbortError') return;
        this.intentToPlay = false;
        this.setState('error');
      }
    }

    pause() {
      if (!this.audio) return;
      this.intentToPlay = false;
      this.playVersion = (this.playVersion || 0) + 1;
      this.audio.pause();
      if (this.dataset.state !== 'ended' && this.dataset.state !== 'error') this.setState(this.audio.currentTime ? 'paused' : 'ready');
    }

    seekTo(value) {
      const clamped = Math.max(0, Math.min(this.duration, value));
      this.seek.value = String(clamped);
      if (this.audio.readyState < 1) {
        this.pendingSeek = clamped;
        // Do not abort the pending play promise or restart an in-flight metadata request.
        if (!this.intentToPlay && this.audio.networkState !== HTMLMediaElement.NETWORK_LOADING) this.audio.load();
      } else {
        this.audio.currentTime = clamped;
      }
      this.updateTime(clamped);
    }

    format(seconds) {
      const safe = Math.max(0, Math.floor(Number(seconds) || 0));
      return Math.floor(safe / 60) + ':' + String(safe % 60).padStart(2, '0');
    }

    updateTime(override) {
      const seconds = override === undefined ? (this.pendingSeek === null ? this.audio.currentTime : this.pendingSeek) : override;
      this.seek.value = String(seconds || 0);
      this.seek.style.setProperty('--fill', (this.duration ? Math.min(100, seconds / this.duration * 100) : 0) + '%');
      this.seek.setAttribute('aria-valuetext', Math.floor(seconds || 0) + ' of ' + Math.floor(this.duration) + ' ' + this.dataset.seconds);
      this.current.textContent = this.format(seconds);
      this.total.textContent = this.format(this.duration);
    }

    syncVolume() {
      const muted = this.audio.muted || this.audio.volume === 0;
      this.dataset.muted = String(muted);
      this.muteButton.setAttribute('aria-label', muted ? this.dataset.unmute : this.dataset.mute);
      this.muteButton.setAttribute('aria-pressed', String(muted));
      this.volume.value = String(muted ? 0 : this.audio.volume);
      this.volume.style.setProperty('--fill', Number(this.volume.value) * 100 + '%');
      this.volume.setAttribute('aria-valuetext', Math.round(Number(this.volume.value) * 100) + '%');
    }

    setState(state) {
      this.dataset.state = state;
      this.toggle.setAttribute('aria-label', state === 'ended' ? this.dataset.replay : this.intentToPlay ? this.dataset.pause : this.dataset.play);
      this.status.textContent = this.dataset[state] || this.dataset.ready;
      this.status.setAttribute('aria-busy', String(state === 'loading'));
    }
  }
  customElements.define('edmt-preview', EdmtPreview);

  // The unchanged long-form descriptions contain cross-origin SoundCloud embeds.
  // Their small official bridge is necessary to coordinate them, not to build our player.
  // Load asynchronously only on pages containing both kinds of preview.
  const connectSoundCloud = () => {
    const frames = document.querySelectorAll('iframe[src*="w.soundcloud.com/player"]');
    if (!document.querySelector('edmt-preview') || !frames.length) return;
    const bind = () => frames.forEach(frame => {
      if (soundCloudWidgets.has(frame) || !window.SC?.Widget) return;
      const widget = window.SC.Widget(frame);
      soundCloudWidgets.set(frame, widget);
      widget.bind(window.SC.Widget.Events.PLAY, () => {
        stopPlayers();
        document.dispatchEvent(new CustomEvent('edmt:preview-start'));
        if (channel) channel.postMessage('play');
      });
      widget.bind(window.SC.Widget.Events.READY, () => {
        if ([...players].some(player => player.intentToPlay)) widget.pause();
      });
    });
    if (window.SC?.Widget) { bind(); return; }
    let script = document.querySelector('script[data-edmt-sc-bridge]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://w.soundcloud.com/player/api.js';
      script.async = true;
      script.dataset.edmtScBridge = 'true';
      document.head.appendChild(script);
    }
    script.addEventListener('load', bind, { once: true });
  };
  document.addEventListener('DOMContentLoaded', connectSoundCloud);
  document.addEventListener('page:loaded', connectSoundCloud);
  if (document.readyState !== 'loading') connectSoundCloud();

})();

